/**
 * Hook métier d'OrDetail.jsx (extrait iter31).
 *
 * Centralise toutes les actions HTTP + state dialogs liés à un OR :
 *  - changeStatut(nouveau)         : PATCH /or/{id}/statut
 *  - finaliserOr(mode)             : POST /or/{id}/finaliser   (+ ouverture PDF si caisse)
 *  - emettreFactureMode(mode)      : POST /or/{id}/emettre-facture
 *  - emettreFacture30j()           : ouvre le dialog d'émission
 *  - archiveOr() / desarchiveOr()  : POST /or/{id}/archive (POST /or/{id}/desarchive)
 *  - uploadPhotos / removePhoto    : gestion photos dépose
 *  - removeOr()                    : DELETE /or/{id}
 *  - sendDevisSms() / copyLink()   : envoi SMS du lien de validation devis
 *  - openPdfInTab / downloadPdf    : PDF devis
 *  - openFacturePdfInTab / downloadFacturePdf : PDF facture (QR-bill)
 *
 * Retourne aussi les états des 3 dialogs (final, emettre, smsResult) et la fonction
 * de matching `STATUT_LABELS` pour les toasts.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { photoCompress } from "@/components/or/photoCompress";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

async function _fetchPdf(url, { open = false, filename = null } = {}) {
  const token = localStorage.getItem("cm_token");
  const r = await fetch(`${BACKEND_URL}${url}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!r.ok) throw new Error("Erreur PDF");
  const blob = await r.blob();
  const u = URL.createObjectURL(blob);
  if (open) {
    window.open(u, "_blank", "noopener");
    setTimeout(() => URL.revokeObjectURL(u), 60000);
  } else if (filename) {
    const a = document.createElement("a");
    a.href = u;
    a.download = filename;
    a.click();
    setTimeout(() => URL.revokeObjectURL(u), 5000);
  }
}

export default function useOrActions({ id, or, fetchOr, isDirty, saveAll, statutLabels }) {
  const navigate = useNavigate();

  // ---- Statut transitions ----
  // Pour devis_envoye / pret, on intercepte pour proposer un choix avec/sans SMS
  // (cf. StatutSmsConfirmDialog + SmsPreviewDialog).
  const [confirmStatutDialog, setConfirmStatutDialog] = useState({ open: false, target: null, finaliserMode: null });
  const [smsPreview, setSmsPreview] = useState({ open: false, to: "", message: "", lienAuto: false, target: null, loading: false, sending: false });
  // iter230 — le texte est éditable dans SmsPreviewDialog
  const updateSmsMessage = (message) => setSmsPreview((s) => ({ ...s, message }));
  // iter119 — Sélection intervenant principal au démarrage OR (cree→commencement)
  const [principalDialog, setPrincipalDialog] = useState({ open: false, target: null });
  // iter346 — Rappel de complétude au démarrage des travaux
  const [completudeDialog, setCompletudeDialog] = useState({ open: false, target: null, manquants: [], intervenantPrincipalId: null });

  const _applyChangeStatut = async (nouveau, { skipSms = false, intervenantPrincipalId = null, smsMessage = null, completudeConfirmee = false } = {}) => {
    try {
      const body = { statut: nouveau, skip_sms: skipSms };
      if (smsMessage) body.sms_message = smsMessage;
      if (intervenantPrincipalId) body.intervenant_principal_id = intervenantPrincipalId;
      if (completudeConfirmee) body.completude_confirmee = true;
      const { data } = await api.patch(`/or/${id}/statut`, body);
      if (data.skipped && data.skipped.length > 0) {
        toast.success(`Statut → ${statutLabels[nouveau]} (${data.skipped.length} étape(s) sautée(s))`);
      } else {
        toast.success(`Statut → ${statutLabels[nouveau]}`);
      }
      if (data.sms?.status === "sent") toast.success("SMS envoyé au client");
      if (data.sms?.status === "skipped_no_phone")
        toast.warning("⚠️ Client sans numéro mobile — SMS non envoyé");
      // Si l'API a renvoyé l'URL publique (cas devis_envoye), on affiche le dialog résultat
      if (data.url) {
        setSmsResult({
          url: data.url,
          to: data.to,
          mocked: data.sms?.status === "mocked" || data.sms?.status === "mocked_preview_blocked",
        });
        setShowSmsDialog(true);
      }
      fetchOr();
    } catch (e) {
      // iter346 — L'OR n'est pas complet : on propose de compléter ou de confirmer
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail?.code === "completude_incomplete") {
        setCompletudeDialog({
          open: true, target: nouveau, manquants: detail.manquants || [],
          intervenantPrincipalId,
        });
        return;
      }
      toast.error(typeof detail === "string" ? detail : detail?.message || "Transition refusée");
    }
  };

  // iter346 — Enregistre ce que l'utilisateur a complété, puis démarre les travaux
  const confirmCompletude = async ({ valeurs }) => {
    const { target, intervenantPrincipalId } = completudeDialog;
    try {
      const patchOr = {};
      const patchClient = {};
      if (valeurs.km_depose) patchOr.km_depose = parseInt(valeurs.km_depose, 10);
      if (valeurs.notes_client) patchOr.notes_client = valeurs.notes_client;
      if (valeurs.heures_estimees) patchOr.heures_estimees = parseFloat(valeurs.heures_estimees);
      if (valeurs.no_sinistre) {
        const refs = (or?.references || []).filter((r) => r.key !== "no_sinistre");
        patchOr.references = [...refs, { key: "no_sinistre", label: "N° de sinistre", value: valeurs.no_sinistre }];
      }
      if (valeurs.client_telephone) patchClient.telephone = valeurs.client_telephone;
      if (valeurs.client_email) patchClient.email = valeurs.client_email;
      if (Object.keys(patchOr).length) await api.patch(`/or/${id}`, patchOr);
      if (Object.keys(patchClient).length && or?.client_id) {
        await api.patch(`/clients/${or.client_id}`, patchClient);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Enregistrement partiel impossible");
    }
    setCompletudeDialog({ open: false, target: null, manquants: [], intervenantPrincipalId: null });
    await _applyChangeStatut(target, { intervenantPrincipalId, completudeConfirmee: true });
  };

  const changeStatut = async (nouveau) => {
    // Statuts qui déclenchent un SMS automatique → propose un choix utilisateur
    if (nouveau === "devis_envoye" || nouveau === "pret") {
      setConfirmStatutDialog({ open: true, target: nouveau, finaliserMode: null });
      return;
    }
    // iter119 — Démarrage de l'OR (passage par 'commencement') = sélection obligatoire du principal
    // Si l'OR n'a pas encore d'intervenant_principal_id ET le nouveau statut est 'commencement'
    // OU qu'un saut vers travaux_effectue/pret passe forcément par 'commencement'.
    const passeParCommencement = (
      nouveau === "commencement" ||
      ["travaux_effectue", "pret"].includes(nouveau)
    );
    if (passeParCommencement && !or?.intervenant_principal_id) {
      // iter128 — Si le user actuel est lui-même un mécanicien, auto-assigner sans dialog.
      try {
        const { data } = await api.get("/atelier/me/intervenant-id");
        if (data?.id) {
          await _applyChangeStatut(nouveau, { intervenantPrincipalId: data.id });
          toast.success(`OR attribué à ${data.label}`);
          return;
        }
      } catch {
        // Pas de mécanicien associé → fallback dialog manuel
      }
      setPrincipalDialog({ open: true, target: nouveau });
      return;
    }
    await _applyChangeStatut(nouveau);
  };

  const confirmPrincipalAndStatut = async (intervenantId) => {
    const target = principalDialog.target;
    setPrincipalDialog({ open: false, target: null });
    await _applyChangeStatut(target, { intervenantPrincipalId: intervenantId });
  };

  const confirmStatutWithoutSms = async () => {
    const { target, finaliserMode } = confirmStatutDialog;
    setConfirmStatutDialog({ open: false, target: null, finaliserMode: null });
    if (finaliserMode) {
      await _doFinaliser(finaliserMode, true);
    } else {
      await _applyChangeStatut(target, { skipSms: true });
    }
  };

  // Charge l'aperçu SMS puis affiche le dialog visuel
  const confirmStatutWithSms = async () => {
    const { target, finaliserMode } = confirmStatutDialog;
    setConfirmStatutDialog({ open: false, target: null, finaliserMode: null });
    setSmsPreview({
      open: true, to: "", message: "", lienAuto: false,
      target: finaliserMode ? `__finaliser_${finaliserMode}__` : target,
      loading: true, sending: false,
    });
    try {
      // Pour devis_envoye → preview type 'devis'. Pour pret ET finaliser → 'pret'.
      const type = target === "devis_envoye" ? "devis" : "pret";
      const { data } = await api.get(`/or/${id}/sms-preview`, { params: { type } });
      setSmsPreview((s) => ({
        ...s,
        open: true,
        to: data.to,
        message: data.message,
        lienAuto: !!data.lien_auto,
        loading: false,
      }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossible de générer l'aperçu SMS");
      setSmsPreview({ open: false, to: "", message: "", lienAuto: false, target: null, loading: false, sending: false });
    }
  };

  const cancelSmsPreview = () => {
    setSmsPreview({ open: false, to: "", message: "", lienAuto: false, target: null, loading: false, sending: false });
  };

  const sendSmsPreview = async () => {
    const target = smsPreview.target;
    setSmsPreview((s) => ({ ...s, sending: true }));
    try {
      // Cas finalisation : on appelle _doFinaliser au lieu de _applyChangeStatut
      if (target && target.startsWith("__finaliser_")) {
        const mode = target.replace("__finaliser_", "").replace(/__$/, "");
        // iter219 — Si l'OR est déjà en statut post-atelier, `/finaliser`
        // refusera (exige `travaux_effectue`). On bascule sur l'endpoint
        // dédié qui envoie juste le SMS sans changement de statut.
        const currentStatut = or?.statut;
        const postAtelier = ["pret", "facture", "paye"];
        if (currentStatut && postAtelier.includes(currentStatut)) {
          const { data } = await api.post(`/or/${id}/sms-vehicule-pret`, { message: smsPreview.message || "" });
          if (data.sms?.status === "sent") {
            toast.success("SMS véhicule prêt envoyé au client");
          } else if (data.sms?.status === "skipped_no_phone") {
            toast.warning("⚠️ Client sans numéro mobile — SMS non envoyé");
          } else if (data.sms?.status === "skipped_disabled_by_admin") {
            toast.info("SMS auto désactivé dans Paramètres");
          } else {
            toast.success("SMS envoyé");
          }
          fetchOr();
        } else {
          await _doFinaliser(mode, false);
        }
      } else {
        await _applyChangeStatut(target, { skipSms: false, smsMessage: smsPreview.message || null });
      }
      setSmsPreview({ open: false, to: "", message: "", lienAuto: false, target: null, loading: false, sending: false });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'envoi SMS");
      setSmsPreview((s) => ({ ...s, sending: false }));
    }
  };

  // ---- Finalisation (statut travaux_effectue → cash | 30j) ----
  const [finalDialog, setFinalDialog] = useState(false);
  const [finalSubmitting, setFinalSubmitting] = useState(false);
  // skipSmsFinal conservé pour rétrocompat — non utilisé dans la nouvelle UI iter45
  // (le choix sans/avec SMS passe désormais par StatutSmsConfirmDialog).
  const [skipSmsFinal, setSkipSmsFinal] = useState(false);

  // Entrée publique : appelée par le bouton "Travaux terminés" du OrFinaliserDialog
  // (iter92 — plus de facturation depuis l'OR : seul le mode "30j" = clôture vers
  // "Factures à émettre" est exposé). On demande d'abord à l'utilisateur s'il a
  // déjà prévenu le client (cohérent avec iter43).
  const chooseFinaliser = (mode) => {
    if (mode !== "30j") return;
    // On garde finalDialog ouvert visuellement masqué par le confirm — pas besoin de le fermer.
    setConfirmStatutDialog({ open: true, target: "pret", finaliserMode: mode });
  };

  // Appel API réel — `skipSms` reçu en argument (et non plus depuis state).
  const _doFinaliser = async (mode, skipSms) => {
    setFinalSubmitting(true);
    try {
      if (isDirty) {
        const ok = await saveAll();
        if (!ok) {
          setFinalSubmitting(false);
          return;
        }
      }
      const { data } = await api.post(`/or/${id}/finaliser`, {
        mode,
        skip_sms: !!skipSms,
      });
      if (mode === "30j") {
        toast.success("Travaux terminés — le dossier est dans « Factures à émettre » (Facturation)");
      } else {
        toast.success(`Facture ${data.facture_numero} créée et encaissée (${mode})`);
      }
      if (data.sms?.status === "sent") toast.success("SMS véhicule prêt envoyé au client");
      if (data.sms?.status === "skipped_no_phone")
        toast.warning("⚠️ Client sans numéro mobile — SMS non envoyé");
      setFinalDialog(false);
      fetchOr();
      if (data.facture_pdf_url) {
        await _fetchPdf(data.facture_pdf_url, { open: true }).catch(() => {});
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de finalisation");
    } finally {
      setFinalSubmitting(false);
    }
  };

  // Conservé en API publique pour rétrocompat (jamais appelée depuis l'UI iter45).
  const finaliserOr = async (mode) => _doFinaliser(mode, skipSmsFinal);

  // ---- Archive / Désarchive ----
  const archiveOr = async () => {
    if (!window.confirm(
      "Archiver cet OR ? Il sera retiré du flux atelier actif mais reste accessible via les Archives."
    )) return;
    try {
      await api.post(`/or/${id}/archive`);
      toast.success("OR archivé");
      navigate("/atelier");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const desarchiveOr = async () => {
    try {
      await api.post(`/or/${id}/desarchive`);
      toast.success("OR désarchivé");
      fetchOr();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  // ---- Photos ----
  const uploadPhotos = async (files, type = "depose") => {
    if (!files || files.length === 0) return;
    try {
      const photos = await Promise.all(Array.from(files).map((f) => photoCompress(f)));
      await api.post(`/or/${id}/photos`, { photos, type });
      toast.success(`${photos.length} photo(s) ajoutée(s)`);
      fetchOr();
    } catch {
      toast.error("Erreur upload");
    }
  };

  const removePhoto = async (index, type = "depose") => {
    if (!window.confirm("Supprimer cette photo ?")) return;
    try {
      await api.delete(`/or/${id}/photos/${index}?type=${type}`);
      fetchOr();
    } catch {
      toast.error("Erreur");
    }
  };

  // ---- Suppression OR ----
  const removeOr = async () => {
    if (!window.confirm("Supprimer définitivement cet OR ?")) return;
    try {
      await api.delete(`/or/${id}`);
      toast.success("OR supprimé");
      navigate("/atelier");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  // ---- Devis SMS + PDF ----
  const [sendingSms, setSendingSms] = useState(false);
  const [smsResult, setSmsResult] = useState(null);
  const [showSmsDialog, setShowSmsDialog] = useState(false);

  // Étape 1 : le bouton "Envoyer le devis par SMS" charge un aperçu via le
  // même endpoint /sms-preview et affiche SmsPreviewDialog.
  const sendDevisSms = async () => {
    setSmsPreview({ open: true, to: "", message: "", lienAuto: false, target: "__devis_sms__", loading: true, sending: false });
    try {
      const { data } = await api.get(`/or/${id}/sms-preview`, { params: { type: "devis" } });
      setSmsPreview({
        open: true,
        to: data.to,
        message: data.message,
        lienAuto: !!data.lien_auto,
        target: "__devis_sms__",
        loading: false,
        sending: false,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossible de générer l'aperçu SMS");
      setSmsPreview({ open: false, to: "", message: "", lienAuto: false, target: null, loading: false, sending: false });
    }
  };

  // iter364 — Envoi du devis par WhatsApp (même endpoint, canal WhatsApp).
  const sendDevisWhatsapp = async () => {
    setSendingSms(true);
    try {
      const { data } = await api.post(`/or/${id}/devis/send-sms`, { send_whatsapp: true });
      const w = data.whatsapp || {};
      if (w.etape === "consentement_demande") {
        toast.info("Accord WhatsApp demandé au client — le devis partira dès sa réponse");
      } else if (w.envoye) {
        toast.success(`Devis envoyé par WhatsApp au ${data.to}`);
      } else {
        toast.warning(w.motif || "WhatsApp indisponible pour ce client");
      }
      await fetchOr?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Envoi WhatsApp impossible");
    } finally { setSendingSms(false); }
  };

  // iter364 — Prévenir le client par WhatsApp que son véhicule est prêt.
  const notifierPretWhatsapp = async () => {
    setSendingSms(true);
    try {
      const { data } = await api.post(`/or/${id}/sms-vehicule-pret`, { canal: "whatsapp" });
      const st = data.sms?.status;
      if (st === "sent_whatsapp") toast.success("Client prévenu par WhatsApp : véhicule prêt");
      else if (st === "whatsapp_impossible") toast.warning(data.sms?.detail || "Client non joignable sur WhatsApp");
      else toast.info(`Message « véhicule prêt » : ${st || "envoyé"}`);
      await fetchOr?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Envoi WhatsApp impossible");
    } finally { setSendingSms(false); }
  };

  // Étape 2 : envoi réel (appelle l'endpoint canonique /devis/send-sms).
  // Utilisée à la fois par sendDevisSms (renvoi/envoi manuel) et confirmStatutWithSms.
  const _doSendDevisSms = async () => {
    setSendingSms(true);
    setSmsPreview((s) => ({ ...s, sending: true }));
    try {
      const { data } = await api.post(`/or/${id}/devis/send-sms`, { message: smsPreview.message || "" });
      setSmsResult({
        url: data.url,
        to: data.to,
        mocked: data.sms?.status === "mocked" || data.sms?.status === "mocked_preview_blocked",
      });
      setSmsPreview({ open: false, to: "", message: "", lienAuto: false, target: null, loading: false, sending: false });
      setShowSmsDialog(true);
      toast.success(
        data.sms?.status === "sent"
          ? `SMS envoyé au ${data.to}`
          : "Lien généré (SMS mocké en preview)"
      );
      fetchOr();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'envoi");
      setSmsPreview((s) => ({ ...s, sending: false }));
    } finally {
      setSendingSms(false);
    }
  };

  // Dispatcher du bouton Send dans SmsPreviewDialog (selon le target courant).
  const handleSmsPreviewSend = async () => {
    if (smsPreview.target === "__devis_sms__") {
      await _doSendDevisSms();
    } else {
      await sendSmsPreview();
    }
  };

  const copyLink = async () => {
    if (!smsResult?.url) return;
    try {
      await navigator.clipboard.writeText(smsResult.url);
      toast.success("Lien copié");
    } catch {
      toast.error("Copie impossible");
    }
  };

  // iter132.i — Copie le lien public devis directement (sans envoyer de SMS).
  // Cas d'usage : coller le lien dans une conversation WhatsApp manuelle.
  const copyDevisPublicLink = async () => {
    try {
      const { data } = await api.get(`/or/${id}/devis/public-link`);
      try {
        await navigator.clipboard.writeText(data.url);
        toast.success("Lien copié — colle-le dans WhatsApp 👍");
      } catch {
        toast.success(`Lien : ${data.url}`);
      }
      fetchOr();  // refresh statut (passe en devis_envoye la première fois)
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de génération du lien");
    }
  };

  const openPdfInTab = () =>
    _fetchPdf(`/api/or/${id}/devis/pdf`, { open: true })
      .catch(() => toast.error("Erreur de génération du PDF"));

  const downloadPdf = () =>
    _fetchPdf(`/api/or/${id}/devis/pdf`, {
      filename: `devis-${or?.numero || id}.pdf`,
    }).catch(() => toast.error("Erreur"));

  const openFacturePdfInTab = () =>
    _fetchPdf(`/api/or/${id}/facture/pdf`, { open: true })
      .then(() => fetchOr())
      .catch(() => toast.error("Erreur de génération du PDF facture"));

  const downloadFacturePdf = () =>
    _fetchPdf(`/api/or/${id}/facture/pdf`, {
      filename: `facture-${or?.facture_numero || id}.pdf`,
    })
      .then(() => fetchOr())
      .catch(() => toast.error("Erreur"));

  return {
    // statut
    changeStatut,
    // statut SMS confirmation (iter43)
    confirmStatutDialog,
    cancelConfirmStatut: () => setConfirmStatutDialog({ open: false, target: null, finaliserMode: null }),
    confirmStatutWithoutSms,
    confirmStatutWithSms,
    // iter119 — Sélection intervenant principal au démarrage OR
    principalDialog,
    cancelPrincipalDialog: () => setPrincipalDialog({ open: false, target: null }),
    // iter346 — rappel de complétude au démarrage
    completudeDialog,
    confirmCompletude,
    cancelCompletudeDialog: () => setCompletudeDialog({ open: false, target: null, manquants: [], intervenantPrincipalId: null }),
    confirmPrincipalAndStatut,
    // SMS preview visuel (iter43)
    smsPreview,
    updateSmsMessage,
    cancelSmsPreview,
    handleSmsPreviewSend,
    // finaliser
    finalDialog, setFinalDialog,
    finalSubmitting,
    skipSmsFinal, setSkipSmsFinal,
    finaliserOr,
    chooseFinaliser,  // iter45 — entry point avec confirmation SMS
    // archive
    archiveOr, desarchiveOr,
    // photos
    uploadPhotos, removePhoto,
    // OR delete
    removeOr,
    // devis SMS + PDF
    sendingSms,
    smsResult,
    showSmsDialog, setShowSmsDialog,
    sendDevisSms,
    sendDevisWhatsapp,
    notifierPretWhatsapp,
    copyLink,
    copyDevisPublicLink,
    openPdfInTab, downloadPdf,
    openFacturePdfInTab, downloadFacturePdf,
  };
}
